import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../../utils/api';
import { useNotification } from '../../contexts/NotificationContext';
import ProtectedRoute from '../../components/ProtectedRoute';
import AdminLayout, { useTheme } from '../../components/AdminLayout';
import Skeleton from '../../components/Skeleton';
import { Link } from 'react-router-dom';
import { PageHeader, DashboardStatCard, ChartCard, TopWorkersCard, WorkerProfileCard } from '../../components/dashboard';
import UtilizationHeatmap from '../../components/dashboard/UtilizationHeatmap';
import UserIcon from '../../components/icons/UserIcon';
import SalonsIcon from '../../components/icons/SalonsIcon';
import CustomersIcon from '../../components/icons/CustomersIcon';
import { AreaChart, Area, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell, PieChart, Pie, Legend, RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar } from 'recharts';

function DashboardContent() {
  const { t } = useTranslation();
  const { showAlert } = useNotification();
  const { T, S } = useTheme();

  const [stats, setStats] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [analyticsLoading, setAnalyticsLoading] = useState(true);

  const [shops, setShops] = useState([]);
  const [branches, setBranches] = useState([]);
  const [selectedShopId, setSelectedShopId] = useState('');
  const [selectedBranchId, setSelectedBranchId] = useState('');
  const [employeeSearch, setEmployeeSearch] = useState('');

  const [activeTab, setActiveTab] = useState('overview'); // overview | branches | employees | services

  useEffect(() => {
    api.getAdminDashboard()
      .then(setStats)
      .catch((err) => showAlert(err?.message || 'Failed to load dashboard'))
      .finally(() => setLoading(false));

    api.getAdminShops().then(setShops).catch((err) => showAlert(err?.message || 'Failed to load shops'));
  }, [showAlert]);

  useEffect(() => {
    if (selectedShopId) {
      api.getBranches(null, selectedShopId).then(setBranches).catch((err) => showAlert(err?.message || 'Failed to load branches'));
    } else {
      setBranches([]);
      setSelectedBranchId('');
    }
  }, [selectedShopId, showAlert]);

  useEffect(() => {
    setAnalyticsLoading(true);
    const sId = selectedShopId || null;
    const bId = selectedBranchId || null;

    api.getAdminAnalytics(sId, bId)
      .then(setAnalytics)
      .catch((err) => showAlert(err?.message || 'Failed to load analytics'))
      .finally(() => setAnalyticsLoading(false));
  }, [selectedShopId, selectedBranchId, showAlert]);

  const QUICK_LINKS = [
    { to: '/admin/users', labelKey: 'admin.users', descKey: 'admin.descUsers' },
    { to: '/admin/shops', labelKey: 'admin.salons', descKey: 'admin.descSalons' },
    { to: '/admin/bookings', labelKey: 'admin.bookings', descKey: 'admin.descBookings' },
    { to: '/admin/products', labelKey: 'admin.products', descKey: 'admin.descProducts' },
    { to: '/admin/time-slots', labelKey: 'admin.timeSlots', descKey: 'admin.descTimeSlots' },
    { to: '/admin/services', labelKey: 'admin.services', descKey: 'admin.descServices' },
    { to: '/admin/bundles', labelKey: 'admin.bundles.title', descKey: 'admin.descBundles' },
    { to: '/admin/seats', labelKey: 'admin.seats', descKey: 'admin.descSeats' },
  ];

  const chartTooltipStyle = {
    backgroundColor: '#ffffff',
    border: '1px solid #e2e8f0',
    borderRadius: '8px',
    boxShadow: '0 10px 15px -3px rgba(0,0,0,0.1)',
    padding: '10px 14px',
    fontSize: '13px',
    fontFamily: "'DM Sans', sans-serif",
    color: T.text,
  };
  const chartMargin = { top: 12, right: 16, bottom: 8, left: 8 };

  const COLORS = ['#0ea5e9', '#3b82f6', '#6366f1', '#8b5cf6', '#a855f7', '#d946ef', '#ec4899', '#f43f5e'];

  const hasData = (arr) => Array.isArray(arr) && arr.length > 0;

  const EmptyChartState = ({ message = 'Waiting for data...' }) => (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: T.muted, fontSize: '0.9rem', fontStyle: 'italic', background: T.bg, borderRadius: '6px', border: `1px dashed ${T.border}` }}>
      {t(message) || message}
    </div>
  );

  const renderTabButton = (tabId, label) => (
    <button
      onClick={() => setActiveTab(tabId)}
      style={{
        background: 'none',
        border: 'none',
        color: activeTab === tabId ? T.orange : T.muted,
        padding: '0.5rem 0',
        cursor: 'pointer',
        fontWeight: activeTab === tabId ? 600 : 500,
        fontFamily: "'DM Sans', sans-serif",
        fontSize: '0.9rem',
        transition: 'color 0.2s',
        marginRight: '1.5rem',
        borderBottom: activeTab === tabId ? `2px solid ${T.orange}` : '2px solid transparent',
      }}
    >
      {label}
    </button>
  );

  const [exporting, setExporting] = useState(false);
  const handleExport = async () => {
    setExporting(true);
    try {
      await api.downloadAdminFinancialReport(selectedShopId || null, selectedBranchId || null);
    } catch (err) {
      showAlert(err?.message || 'Failed to download report');
    } finally {
      setExporting(false);
    }
  };

  return (
    <AdminLayout role="admin">
      <style>{`
        .hover-card { transition: transform 0.2s cubic-bezier(0.4, 0, 0.2, 1), box-shadow 0.2s; }
        .hover-card:hover { transform: translateY(-3px); box-shadow: 0 10px 15px -3px rgba(0,0,0,0.08); border-color: ${T.orange}; }
        .overview-with-sidebar { display: grid; grid-template-columns: minmax(0, 1fr) 320px; gap: 1.5rem; align-items: start; }
        @media (max-width: 960px) { .overview-with-sidebar { grid-template-columns: 1fr; } .overview-sidebar { order: -1; } }
      `}</style>

      <PageHeader
        title={t('admin.dashboardTitle')}
        subtitle={t('dashboard.subtitle') || 'Get an overview of your salons, bookings, and performance.'}
        actions={
          <>
            {shops.length > 0 && (
              <select
                value={selectedShopId}
                onChange={(e) => { setSelectedShopId(e.target.value); setSelectedBranchId(''); }}
                style={{ ...S.select, marginBottom: 0, width: 'auto', minWidth: 140 }}
              >
                <option value="">{t('admin.allSalons')}</option>
                {shops.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
            )}
            {branches.length > 0 && (
              <select
                value={selectedBranchId}
                onChange={(e) => setSelectedBranchId(e.target.value)}
                style={{ ...S.select, marginBottom: 0, width: 'auto', minWidth: 140 }}
              >
                <option value="">{t('admin.allBranches') || 'All Branches'}</option>
                {branches.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
              </select>
            )}
            <button
              onClick={handleExport}
              disabled={exporting}
              style={{
                display: 'flex', alignItems: 'center', gap: '0.5rem',
                padding: '0.5rem 1rem', background: '#fff', border: `1px solid ${T.border}`,
                borderRadius: 8, fontFamily: "'DM Sans', sans-serif", fontSize: '0.875rem',
                fontWeight: 600, color: T.orange, cursor: exporting ? 'wait' : 'pointer',
              }}
            >
              <span>⬇</span> {t('dashboard.downloadReport') || t('dashboard.exportReport') || 'Download Report'}
            </button>
          </>
        }
      />

      <div style={{ display: 'flex', gap: 0, marginBottom: '1.5rem', borderBottom: `1px solid ${T.border}` }}>
        {renderTabButton('overview', t('dashboard.overview') || 'Overview')}
        {renderTabButton('services', t('dashboard.services') || 'Services & Categories')}
        {renderTabButton('branches', t('dashboard.branches') || 'Branch Performance')}
        {renderTabButton('employees', t('dashboard.employees') || 'Employee Performance')}
      </div>

      {loading ? <Skeleton height="120px" count={2} /> : stats && activeTab === 'overview' && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '1rem', marginBottom: '2rem' }}>
          <DashboardStatCard
            icon="💰"
            iconColor="#0ea5e9"
            label={t('admin.revenue') || 'Total Revenue'}
            value={analyticsLoading ? '...' : (analytics?.overview?.total_revenue ? `EGP ${analytics.overview.total_revenue.toFixed(2)}` : 'EGP 0.00')}
            trend={analytics?.overview?.growth_percentage}
          />
          <DashboardStatCard
            icon={<CustomersIcon size={24} />}
            iconColor="#6366f1"
            label={t('admin.customers') || 'Total Customers'}
            value={analyticsLoading ? '...' : (analytics?.overview?.total_customers ?? 0)}
          />
          <DashboardStatCard
            icon="✓"
            iconColor="#16a34a"
            label={t('admin.totalBookings')}
            value={stats?.total_bookings ?? '-'}
          />
          <DashboardStatCard
            icon={<UserIcon size={24} color="#64748b" />}
            iconColor="#64748b"
            label={t('admin.users')}
            value={stats?.regular_users_count ?? stats?.total_users ?? '-'}
          />
          <DashboardStatCard
            icon={<SalonsIcon size={24} color="#d97706" />}
            iconColor="#d97706"
            label={t('admin.salons')}
            value={stats?.total_shops ?? '-'}
          />
        </div>
      )}

      {/* Utilization Heatmap (Option A optimization) */}
      {activeTab === 'overview' && selectedBranchId && (
        <div style={{ marginBottom: '1.5rem' }}>
          <UtilizationHeatmap branchId={selectedBranchId} role="admin" days={60} />
        </div>
      )}

      {/* OVERVIEW TAB */}
      {activeTab === 'overview' && (
        <div className="overview-with-sidebar" style={{ marginBottom: '1.5rem' }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: '1.5rem', minWidth: 0 }}>
          {/* 1. Revenue over Time - larger for time-series */}
          <ChartCard title="Revenue over Time" height={300} fullWidth={false}>
            {analyticsLoading ? <Skeleton height="300px" /> : !hasData(analytics?.revenue_by_date) ? <EmptyChartState /> : (
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={analytics.revenue_by_date} margin={chartMargin}>
                  <defs>
                    <linearGradient id="colorRev" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor={T.orange} stopOpacity={0.4} />
                      <stop offset="100%" stopColor={T.orange} stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
                  <XAxis dataKey="date" stroke={T.muted} fontSize={12} tickLine={false} />
                  <YAxis stroke={T.muted} fontSize={12} tickLine={false} tickFormatter={(v) => `EGP ${v}`} domain={['auto', 'auto']} />
                  <Tooltip contentStyle={chartTooltipStyle} formatter={(v) => [`EGP ${Number(v).toFixed(2)}`, 'Revenue']} cursor={{ stroke: T.orange, strokeWidth: 1, strokeDasharray: '4 4' }} />
                  <Area type="monotone" dataKey="revenue" stroke={T.orange} strokeWidth={2} fillOpacity={1} fill="url(#colorRev)" />
                </AreaChart>
              </ResponsiveContainer>
            )}
          </ChartCard>

          {/* 2. Bookings over Time */}
          <ChartCard title="Bookings over Time" height={300} fullWidth={false}>
            {analyticsLoading ? <Skeleton height="300px" /> : !hasData(analytics?.bookings_by_date) ? <EmptyChartState /> : (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={analytics.bookings_by_date} margin={chartMargin} barCategoryGap="20%" barSize={40}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
                  <XAxis dataKey="date" stroke={T.muted} fontSize={12} tickLine={false} />
                  <YAxis stroke={T.muted} fontSize={12} tickLine={false} allowDecimals={false} domain={[0, 'auto']} />
                  <Tooltip contentStyle={chartTooltipStyle} formatter={(v) => [v, t('admin.totalBookings')]} cursor={{ fill: 'rgba(14,165,233,0.06)' }} />
                  <Bar dataKey="count" fill={T.orange} radius={[6, 6, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </ChartCard>

          {/* 3. Booking Status - donut */}
          <ChartCard title="Booking Status" height={280} fullWidth={false}>
            {analyticsLoading ? <Skeleton height="280px" /> : !hasData(analytics?.booking_status) ? <EmptyChartState /> : (
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={analytics.booking_status}
                    cx="50%"
                    cy="50%"
                    innerRadius="55%"
                    outerRadius="75%"
                    paddingAngle={2}
                    dataKey="value"
                    nameKey="name"
                    stroke="#ffffff"
                    strokeWidth={2}
                  >
                    {analytics.booking_status.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip contentStyle={chartTooltipStyle} formatter={(v, name) => [`${v} (${((v / analytics.booking_status.reduce((a, x) => a + x.value, 0)) * 100).toFixed(0)}%)`, name]} />
                  <Legend layout="horizontal" align="center" verticalAlign="bottom" wrapperStyle={{ paddingTop: 12 }} fontSize={12} />
                </PieChart>
              </ResponsiveContainer>
            )}
          </ChartCard>

          {/* 4. Customer Demographics (Gender) */}
          <ChartCard title="Customer Demographics (Gender)" height={280} fullWidth={false}>
            {analyticsLoading ? <Skeleton height="280px" /> : !hasData(analytics?.customer_demographics?.gender) ? <EmptyChartState /> : (
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={analytics.customer_demographics.gender}
                    cx="50%"
                    cy="50%"
                    innerRadius="45%"
                    outerRadius="70%"
                    paddingAngle={2}
                    dataKey="value"
                    nameKey="name"
                    stroke="#ffffff"
                    strokeWidth={2}
                  >
                    {analytics.customer_demographics.gender.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip contentStyle={chartTooltipStyle} formatter={(v, name) => [`${v} (${((v / analytics.customer_demographics.gender.reduce((a, x) => a + x.value, 0)) * 100).toFixed(0)}%)`, name]} />
                  <Legend layout="horizontal" align="center" verticalAlign="bottom" wrapperStyle={{ paddingTop: 12 }} fontSize={12} />
                </PieChart>
              </ResponsiveContainer>
            )}
          </ChartCard>
          </div>

          {/* Top Workers - middle right sidebar */}
          <div className="overview-sidebar" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'sticky', top: '2rem' }}>
            <TopWorkersCard
              workers={analytics?.top_artists || []}
              onViewAll={() => setActiveTab('employees')}
              loading={analyticsLoading}
            />
          </div>
        </div>
      )}

      {/* SERVICES TAB */}
      {activeTab === 'services' && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(340px, 1fr))', gap: '1.5rem', marginBottom: '1.5rem' }}>

          {/* 6. Revenue by Category */}
          <ChartCard title="Revenue by Category" height={340}>
            {analyticsLoading ? <Skeleton height="340px" /> : !hasData(analytics?.revenue_by_category) ? <EmptyChartState /> : (
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={analytics.revenue_by_category} cx="50%" cy="50%" innerRadius="40%" outerRadius="70%" paddingAngle={2} dataKey="revenue" nameKey="name" stroke="#ffffff" strokeWidth={2}>
                    {analytics.revenue_by_category.map((entry, index) => <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />)}
                  </Pie>
                  <Tooltip formatter={(value) => [`EGP ${Number(value).toFixed(2)}`, 'Revenue']} contentStyle={chartTooltipStyle} />
                  <Legend layout="horizontal" align="center" verticalAlign="bottom" wrapperStyle={{ paddingTop: 12 }} />
                </PieChart>
              </ResponsiveContainer>
            )}
          </ChartCard>

          {/* 7. Most Popular Services */}
          <ChartCard title="Most Popular Services" height={340} fullWidth={true}>
            {analyticsLoading ? <Skeleton height="340px" /> : !hasData(analytics?.service_popularity) ? <EmptyChartState /> : (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={analytics.service_popularity} layout="vertical" margin={{ left: 90, right: 24, top: 8, bottom: 8 }} barSize={28}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" horizontal={true} vertical={false} />
                  <XAxis type="number" stroke={T.muted} fontSize={12} tickLine={false} allowDecimals={false} />
                  <YAxis type="category" dataKey="name" stroke={T.muted} fontSize={12} width={90} tickLine={false} />
                  <Tooltip contentStyle={chartTooltipStyle} cursor={{ fill: 'rgba(14,165,233,0.06)' }} />
                  <Bar dataKey="count" fill={T.orange} radius={[0, 6, 6, 0]} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </ChartCard>

        </div>
      )}

      {/* BRANCHES TAB */}
      {activeTab === 'branches' && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(340px, 1fr))', gap: '1.5rem', marginBottom: '2rem' }}>

          {/* 8. Revenue by Branch */}
          <ChartCard title="Revenue by Branch" height={300}>
            {analyticsLoading ? <Skeleton height="300px" /> : !hasData(analytics?.branches) ? <EmptyChartState message="No branch data available." /> : (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={analytics.branches} margin={chartMargin} barCategoryGap="20%" barSize={36}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
                  <XAxis dataKey="name" stroke={T.muted} fontSize={12} tickLine={false} />
                  <YAxis stroke={T.muted} fontSize={12} tickLine={false} tickFormatter={(v) => `EGP ${v}`} />
                  <Tooltip formatter={(val) => [`EGP ${Number(val).toFixed(2)}`, 'Revenue']} contentStyle={chartTooltipStyle} cursor={{ fill: 'rgba(59,130,246,0.06)' }} />
                  <Bar dataKey="revenue" fill={COLORS[1]} radius={[6, 6, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </ChartCard>

          {/* 9. Bookings by Branch */}
          <ChartCard title="Bookings by Branch" height={300}>
            {analyticsLoading ? <Skeleton height="300px" /> : !hasData(analytics?.branches) ? <EmptyChartState message="No branch data available." /> : (
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={analytics.branches} cx="50%" cy="50%" innerRadius="50%" outerRadius="70%" paddingAngle={2} dataKey="bookings" nameKey="name" stroke="#ffffff" strokeWidth={2}>
                    {analytics.branches.map((entry, index) => <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />)}
                  </Pie>
                  <Tooltip contentStyle={chartTooltipStyle} />
                  <Legend layout="horizontal" align="center" verticalAlign="bottom" wrapperStyle={{ paddingTop: 12 }} />
                </PieChart>
              </ResponsiveContainer>
            )}
          </ChartCard>

          {analytics?.branches?.length > 0 && (
            <div style={{ ...S.card, gridColumn: '1 / -1' }}>
              <h2 style={{ fontFamily: "'DM Sans', sans-serif", fontSize: '1rem', fontWeight: 600, color: T.text, marginBottom: '1rem' }}>Branch Performance Overview</h2>
              <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left', fontSize: '0.9rem' }}>
                <thead>
                  <tr style={{ borderBottom: `2px solid ${T.border}` }}>
                    <th style={{ padding: '0.8rem 0', color: T.muted }}>Branch</th>
                    <th style={{ padding: '0.8rem 0', color: T.muted }}>Rating</th>
                    <th style={{ padding: '0.8rem 0', color: T.muted }}>Total Bookings</th>
                    <th style={{ padding: '0.8rem 0', color: T.muted }}>Revenue</th>
                  </tr>
                </thead>
                <tbody>
                  {analytics.branches.map(b => (
                    <tr key={b.branch_id} style={{ borderBottom: `1px solid ${T.border}` }}>
                      <td style={{ padding: '0.8rem 0', color: T.white }}>{b.name}</td>
                      <td style={{ padding: '0.8rem 0', color: '#d97706' }}>★ {b.rating}</td>
                      <td style={{ padding: '0.8rem 0', color: T.text }}>{b.bookings}</td>
                      <td style={{ padding: '0.8rem 0', color: '#16a34a', fontWeight: 'bold' }}>EGP {b.revenue.toFixed(2)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* EMPLOYEES TAB - Employee Performance Design */}
      {activeTab === 'employees' && (
        <div>
          {/* Employee Performance Header */}
          <div style={{ marginBottom: '1.5rem' }}>
            <h1 style={{ fontFamily: "'DM Sans', sans-serif", fontSize: '1.75rem', fontWeight: 700, color: '#1e293b', marginBottom: '0.25rem' }}>
              {t('dashboard.employeePerformance') || 'Employee Performance'}
            </h1>
            <p style={{ fontFamily: "'DM Sans', sans-serif", fontSize: '0.9rem', color: T.muted, marginBottom: '1rem' }}>
              {t('dashboard.employeePerformanceSubtitle') || 'Monitor team metrics, individual achievements, and branch efficiency.'}
            </p>
            <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
              <button onClick={handleExport} disabled={exporting} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.5rem 1rem', background: '#fff', border: `1px solid ${T.border}`, borderRadius: 8, fontFamily: "'DM Sans', sans-serif", fontSize: '0.875rem', fontWeight: 600, color: T.orange, cursor: exporting ? 'wait' : 'pointer' }}>
                <span>⬇</span> {t('dashboard.downloadReport') || t('dashboard.exportReport') || 'Download Report'}
              </button>
              <Link to="/admin/users" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.5rem 1.25rem', background: T.orange, color: '#fff', border: 'none', borderRadius: 8, fontFamily: "'DM Sans', sans-serif", fontSize: '0.875rem', fontWeight: 600, textDecoration: 'none' }}>
                <span>+</span> {t('dashboard.addEmployee') || 'Add Employee'}
              </Link>
            </div>
          </div>

          {/* KPI Cards: Avg Team Rating, Top Performer, Tasks Completed */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem', marginBottom: '1.5rem' }}>
            <DashboardStatCard
              icon="★"
              iconColor="#0ea5e9"
              label={t('dashboard.avgTeamRating') || 'Avg Team Rating'}
              value={analytics?.top_artists?.length ? `${(analytics.top_artists.reduce((s, a) => s + (a.rating || 0), 0) / analytics.top_artists.length).toFixed(1)}/5.0` : '—'}
              trend={5.2}
            />
            <DashboardStatCard
              icon="🏆"
              iconColor="#6366f1"
              label={t('dashboard.topPerformer') || 'Top Performer'}
              value={analytics?.top_artists?.[0]?.artist_name || '—'}
              subtitle={analytics?.top_artists?.[0] ? `EGP ${analytics.top_artists[0].revenue?.toFixed(0)} revenue` : undefined}
              trend={5.2}
            />
            <DashboardStatCard
              icon="✓"
              iconColor="#16a34a"
              label={t('dashboard.tasksCompleted') || 'Tasks Completed'}
              value={analytics?.top_artists?.reduce((s, a) => s + (a.count || 0), 0) ?? 0}
              trend={12}
            />
          </div>

          {/* Top Workers Leaderboard */}
          {analytics?.top_artists?.length > 0 && (
            <div style={{ ...S.card, marginBottom: '1.5rem' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                <h2 style={{ fontFamily: "'DM Sans', sans-serif", fontSize: '1rem', fontWeight: 600, color: '#1e293b' }}>{t('dashboard.topWorkersLeaderboard') || 'Top Workers Leaderboard'}</h2>
                <Link to="/admin/users" style={{ fontSize: '0.85rem', color: T.orange, fontWeight: 600, textDecoration: 'none' }}>{t('dashboard.viewAll') || 'View All'}</Link>
              </div>
              <div style={{ display: 'flex', gap: '1rem', overflowX: 'auto', paddingBottom: '0.5rem', flexWrap: 'wrap' }}>
                {analytics.top_artists.slice(0, 4).map((artist, idx) => (
                  <WorkerProfileCard key={artist.artist_id} worker={artist} rank={idx + 1} />
                ))}
              </div>
            </div>
          )}

          {/* Charts Row: Top Workers by Revenue, Top Workers by Volume */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(340px, 1fr))', gap: '1.5rem', marginBottom: '1.5rem' }}>
            <ChartCard title="Top Workers by Revenue" height={280}>
              {analyticsLoading ? <Skeleton height="280px" /> : !hasData(analytics?.top_artists) ? <EmptyChartState /> : (
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={analytics.top_artists.slice(0, 6)} layout="vertical" margin={{ left: 90, right: 24 }} barSize={28}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" horizontal={true} vertical={false} />
                    <XAxis type="number" stroke={T.muted} fontSize={12} tickLine={false} tickFormatter={(v) => `EGP ${v}`} />
                    <YAxis type="category" dataKey="artist_name" stroke={T.muted} fontSize={12} width={90} tickLine={false} />
                    <Tooltip formatter={(val) => [`EGP ${Number(val).toFixed(2)}`, 'Revenue']} contentStyle={chartTooltipStyle} cursor={{ fill: 'rgba(14,165,233,0.06)' }} />
                    <Bar dataKey="revenue" fill={T.orange} radius={[0, 6, 6, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </ChartCard>
            <ChartCard title="Top Workers by Volume" height={280}>
              {analyticsLoading ? <Skeleton height="280px" /> : !hasData(analytics?.top_artists) ? <EmptyChartState /> : (
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={analytics.top_artists.slice(0, 6)} layout="vertical" margin={{ left: 90, right: 24 }} barSize={28}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" horizontal={true} vertical={false} />
                    <XAxis type="number" stroke={T.muted} fontSize={12} tickLine={false} allowDecimals={false} />
                    <YAxis type="category" dataKey="artist_name" stroke={T.muted} fontSize={12} width={90} tickLine={false} />
                    <Tooltip contentStyle={chartTooltipStyle} cursor={{ fill: 'rgba(59,130,246,0.06)' }} />
                    <Bar dataKey="count" fill={COLORS[1]} radius={[0, 6, 6, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </ChartCard>
          </div>

          {/* Detailed Performance + Skill Assessment Row */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: '1.5rem', marginBottom: '1.5rem' }}>
            {/* Detailed Performance Table */}
            <div style={S.card}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem', flexWrap: 'wrap', gap: '0.75rem' }}>
                <h2 style={{ fontFamily: "'DM Sans', sans-serif", fontSize: '1rem', fontWeight: 600, color: '#1e293b' }}>{t('dashboard.detailedPerformance') || 'Detailed Performance'}</h2>
                <input
                  type="text"
                  placeholder={t('dashboard.searchEmployee') || 'Search employee...'}
                  value={employeeSearch}
                  onChange={(e) => setEmployeeSearch(e.target.value)}
                  style={{ ...S.input, marginBottom: 0, width: 220, paddingLeft: '2rem', backgroundImage: 'url("data:image/svg+xml,%3Csvg xmlns=\'http://www.w3.org/2000/svg\' fill=\'none\' viewBox=\'0 0 24 24\' stroke=\'%2364748b\' width=\'16\' height=\'16\'%3E%3Cpath strokeLinecap=\'round\' strokeLinejoin=\'round\' strokeWidth=\'2\' d=\'M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z\' /%3E%3C/svg%3E")', backgroundRepeat: 'no-repeat', backgroundPosition: '10px center' }}
                />
              </div>
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.9rem', minWidth: '640px' }}>
                  <thead>
                    <tr style={{ borderBottom: `2px solid ${T.border}` }}>
                      <th style={{ ...S.th, padding: '0.75rem 0' }}>{t('dashboard.employee') || 'EMPLOYEE'}</th>
                      <th style={{ ...S.th, padding: '0.75rem 0' }}>{t('dashboard.roleBranch') || 'ROLE & BRANCH'}</th>
                      <th style={{ ...S.th, padding: '0.75rem 0' }}>{t('dashboard.monthlyTarget') || 'MONTHLY TARGET'}</th>
                      <th style={{ ...S.th, padding: '0.75rem 0' }}>{t('dashboard.attendance') || 'ATTENDANCE'}</th>
                      <th style={{ ...S.th, padding: '0.75rem 0' }}>{t('dashboard.feedback') || 'FEEDBACK'}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(analytics?.top_artists || [])
                      .filter(a => !employeeSearch || a.artist_name?.toLowerCase().includes(employeeSearch.toLowerCase()))
                      .map(a => {
                        const targetPct = a.target_revenue ? Math.min(100, (a.revenue / a.target_revenue) * 100) : 0;
                        const attPct = Number(a.attendance) || 0;
                        return (
                          <tr key={a.artist_id} style={{ borderBottom: `1px solid ${T.border}` }}>
                            <td style={{ padding: '0.75rem 0' }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                                <div style={{ width: 36, height: 36, borderRadius: '50%', background: T.orange, color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 600, fontSize: '0.9rem' }}>{a.artist_name?.[0]?.toUpperCase() || '?'}</div>
                                <div>
                                  <div style={{ fontWeight: 600, color: '#1e293b' }}>{a.artist_name}</div>
                                  <div style={{ fontSize: '0.75rem', color: T.muted }}>ID: #{a.artist_id}</div>
                                </div>
                              </div>
                            </td>
                            <td style={{ padding: '0.75rem 0', color: T.text }}>—</td>
                            <td style={{ padding: '0.75rem 0' }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                <div style={{ flex: 1, maxWidth: 80, background: T.bg, height: 8, borderRadius: 4, overflow: 'hidden' }}>
                                  <div style={{ width: `${targetPct}%`, height: '100%', background: targetPct >= 80 ? '#16a34a' : targetPct >= 50 ? T.orange : '#94a3b8', borderRadius: 4 }} />
                                </div>
                                <span style={{ fontSize: '0.85rem', fontWeight: 600, color: '#1e293b' }}>{targetPct.toFixed(0)}%</span>
                              </div>
                            </td>
                            <td style={{ padding: '0.75rem 0' }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                <div style={{ flex: 1, maxWidth: 80, background: T.bg, height: 8, borderRadius: 4, overflow: 'hidden' }}>
                                  <div style={{ width: `${Math.min(100, attPct)}%`, height: '100%', background: attPct >= 80 ? '#16a34a' : attPct >= 50 ? '#fbbf24' : '#94a3b8', borderRadius: 4 }} />
                                </div>
                                <span style={{ fontSize: '0.85rem', fontWeight: 600, color: '#1e293b' }}>{attPct.toFixed(0)}%</span>
                              </div>
                            </td>
                            <td style={{ padding: '0.75rem 0', color: '#d97706', fontWeight: 600 }}>{a.rating?.toFixed(1) || '0'}/5.0</td>
                          </tr>
                        );
                      })}
                  </tbody>
                </table>
                {(!analytics?.top_artists?.length || analytics.top_artists.filter(a => !employeeSearch || a.artist_name?.toLowerCase().includes(employeeSearch.toLowerCase())).length === 0) && (
                  <div style={{ textAlign: 'center', padding: '2rem', color: T.muted, fontSize: '0.9rem' }}>{t('dashboard.noEmployeesMatch') || 'No employees match your search.'}</div>
                )}
              </div>
            </div>

            {/* Skill Assessment Radar Chart */}
            <div style={S.card}>
              <h2 style={{ fontFamily: "'DM Sans', sans-serif", fontSize: '1rem', fontWeight: 600, color: '#1e293b', marginBottom: '0.5rem' }}>{t('dashboard.skillAssessment') || 'Skill Assessment'}</h2>
              <p style={{ fontSize: '0.85rem', color: T.muted, marginBottom: '1rem' }}>
                {t('dashboard.skillAssessmentDesc') || 'Visual breakdown of'} {analytics?.top_artists?.[0] ? <span style={{ color: T.orange, fontWeight: 600 }}>{analytics.top_artists[0].artist_name}</span> : '—'}{"'"}s {t('dashboard.coreCompetencies') || 'core competencies'}.
              </p>
              {analytics?.top_artists?.[0] ? (
                <ResponsiveContainer width="100%" height={260}>
                  <RadarChart data={[
                    { subject: t('dashboard.quality') || 'Quality', value: Math.round((analytics.top_artists[0].rating || 0) / 5 * 100), fullMark: 100 },
                    { subject: t('dashboard.communication') || 'Comm.', value: Math.round((analytics.top_artists[0].attendance || 0) * 0.9), fullMark: 100 },
                    { subject: t('dashboard.reliability') || 'Reliability', value: Math.round((analytics.top_artists[0].attendance || 0)), fullMark: 100 },
                  ]} cx="50%" cy="50%" outerRadius="70%">
                    <PolarGrid stroke="#e2e8f0" />
                    <PolarAngleAxis dataKey="subject" tick={{ fill: T.muted, fontSize: 12 }} />
                    <PolarRadiusAxis angle={90} domain={[0, 100]} tick={{ fill: T.muted, fontSize: 10 }} />
                    <Radar name="Score" dataKey="value" stroke={T.orange} fill={T.orange} fillOpacity={0.4} strokeWidth={2} />
                  </RadarChart>
                </ResponsiveContainer>
              ) : (
                <div style={{ height: 260, display: 'flex', alignItems: 'center', justifyContent: 'center', color: T.muted, fontSize: '0.9rem', background: T.bg, borderRadius: 8, border: `1px dashed ${T.border}` }}>
                  {t('dashboard.noDataForSkillAssessment') || 'No data for skill assessment.'}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {activeTab === 'overview' && (
        <div className="admin-dashboard-quicklinks" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: '1rem', marginTop: '2rem' }}>
          {QUICK_LINKS.map(({ to, labelKey, descKey }) => (
            <Link
              key={to}
              to={to}
              style={{ ...S.card, display: 'block', textDecoration: 'none', transition: 'transform 0.2s, box-shadow 0.2s' }}
              className="hover-card"
            >
              <h2 style={{ fontFamily: "'DM Sans', sans-serif", fontSize: '1.1rem', fontWeight: 600, color: T.text, marginBottom: '0.4rem' }}>
                {t(labelKey)}
              </h2>
              <p style={{ fontSize: '0.82rem', color: T.muted }}>{t(descKey)}</p>
            </Link>
          ))}
        </div>
      )}
    </AdminLayout>
  );
}

export default function Dashboard() {
  return <ProtectedRoute requireSuperAdmin><DashboardContent /></ProtectedRoute>;
}
